import React, { useMemo } from "react";
import { createHeaderLayoutElement } from "../component-objects/headerLayout";
import { createIconButtonElement } from "../component-objects/iconButton";
import { createLogoElement } from "../component-objects/logo";
import { createNavItemsElement } from "../component-objects/navItems";
import { createSearchBarElement } from "../component-objects/searchBar";
import { ACTION_BUTTONS_CSS } from "../components/header-components/ActionButtons";
import {
	HeaderComponent,
	HeaderElement,
	HeaderState,
	ViewportSize,
} from "../types";
import { renderIcon } from "../utils/icons";

// Event handler registry type
type EventHandlerRegistry = {
	[handlerName: string]: (...args: any[]) => void;
};

// CSS injection utility
class CSSInjector {
	private static injectedCSS = new Set<string>();
	private static styleElement: HTMLStyleElement | null = null;

	static injectCSS(css: string, elementId: string): void {
		if (!css || this.injectedCSS.has(elementId)) return;

		if (!this.styleElement) {
			this.styleElement = document.createElement("style");
			this.styleElement.type = "text/css";
			this.styleElement.id = "header-elements-styles";
			document.head.appendChild(this.styleElement);
		}

		// Add element-specific CSS
		const prefixedCSS = this.prefixCSS(css, elementId);
		this.styleElement.textContent += `\n/* ${elementId} */\n${prefixedCSS}\n`;
		this.injectedCSS.add(elementId);
	}

	private static prefixCSS(css: string, elementId: string): string {
		// Simple CSS prefixing - could be enhanced with a proper CSS parser
		return css.replace(/\.([a-zA-Z][a-zA-Z0-9\-_]*)/g, `.${elementId}-$1`);
	}

	static clearCSS(): void {
		if (this.styleElement) {
			this.styleElement.textContent = "";
			this.injectedCSS.clear();
		}
	}
}

// Renderer configuration
interface RendererConfig {
	eventHandlers?: EventHandlerRegistry;
	selectedElementId?: string;
	onElementClick?: (element: HeaderElement) => void;
	onElementRemove?: (elementId: string) => void;
}

// Main renderer function
export const RenderHeaderElement = (
	element: HeaderElement,
	config: RendererConfig = {}
): React.ReactElement => {
	const {
		eventHandlers = {},
		selectedElementId,
		onElementClick,
		onElementRemove,
	} = config;

	// Inject CSS for this element
	// useEffect(() => {
	//     if (element.css) {
	//         CSSInjector.injectCSS(element.css, element.id);
	//     }
	// }, [element.css, element.id]);

	// Build props for the element
	const buildProps = (element: HeaderElement): Record<string, any> => {
		const props: Record<string, any> = {
			key: element.id,
			id: element.id,
			...element.props,
		};

		// Apply className with element ID prefix for CSS scoping
		if (element.className) {
			props.className = element.className;
			// .split(' ')
			// .map(cls => `${element.id}-${cls}`)
			// .join(' ');
		}

		// Add selection class
		if (selectedElementId === element.id) {
			props.className = `${props.className || ""} ${
				element.id
			}-selected`.trim();
		}

		// Apply inline styles
		if (element.style) {
			props.style = { ...element.style };
		}

		// Handle event handlers
		if (element.props) {
			Object.entries(element.props).forEach(([key, value]) => {
				if (key.startsWith("on") && typeof value === "string") {
					// Map string handler names to actual functions
					if (eventHandlers[value]) {
						props[key] = eventHandlers[value];
					} else if (key === "onClick" && onElementClick) {
						props[key] = (e: React.MouseEvent) => {
							e.stopPropagation();
							onElementClick(element);
						};
					}
				}
			});
		}

		// Handle special data-driven events
		if (element.data) {
			// Handle onClick if defined in data
			if (element.data.onClick && eventHandlers[element.data.onClick]) {
				props.onClick = eventHandlers[element.data.onClick];
			}

			// Handle onRemove if defined in data
			if (element.data.onRemove && onElementRemove) {
				const removeHandler = () => onElementRemove(element.id);
				// Store remove handler for child elements to use
				props["data-remove-handler"] = removeHandler;
			}
		}

		return props;
	};

	// Handle special element types
	const handleSpecialElements = (
		element: HeaderElement
	): React.ReactElement | null => {
		switch (element.type) {
			case "Image":
				return React.createElement("img", {
					...buildProps(element),
					src: element.data.src || element.props?.src,
					alt: element.data.alt || element.props?.alt || "",
				});

			case "ActionButton":
				const buttonProps = buildProps(element);

				// Handle remove button specifically
				if (element.data.action === "remove" && onElementRemove) {
					buttonProps.onClick = (e: React.MouseEvent) => {
						e.stopPropagation();
						onElementRemove(
							element.id.replace("-remove-button", "")
						);
					};
				}

				// Handle edit button
				if (element.data.action === "edit" && onElementClick) {
					buttonProps.onClick = (e: React.MouseEvent) => {
						e.stopPropagation();
						// Find parent element for editing
						const parentId = element.id.replace("-edit-button", "");
						onElementClick({ ...element, id: parentId });
					};
				}

				return React.createElement(
					"button",
					buttonProps,
					...(element.children?.map((child) =>
						RenderHeaderElement(child, config)
					) || [])
				);

			case "Icon":
				return React.createElement(
					"span",
					buildProps(element),
					renderIcon(element.data.icon || "", element.props?.size)
					// element.data.icon || ''
				);

			case "SearchInput":
				return React.createElement("input", {
					...buildProps(element),
					type: element.props?.type || "text",
					placeholder:
						element.data.placeholder ||
						element.props?.placeholder ||
						"",
				});

			default:
				return null;
		}
	};

	// Check for special element types first
	const specialElement = handleSpecialElements(element);
	if (specialElement) {
		return specialElement;
	}

	// Render children
	const children =
		element.children?.map((child) => RenderHeaderElement(child, config)) ||
		[];

	// Handle text content from data
	if (element.data.text && children.length === 0) {
		children.push(element.data.text);
	}

	// Handle icon content
	if (element.data.icon && children.length === 0) {
		children.push(element.data.icon);
	}

	// Create the React element
	return React.createElement(
		element.tagName,
		buildProps(element),
		...children
	);
};

// Hook for rendering multiple elements
export const useHeaderElementRenderer = (
	elements: HeaderElement[],
	config: RendererConfig = {}
) => {
	// Cleanup CSS on unmount
	// useEffect(() => {
	//     return () => {
	//         if (elements.length === 0) {
	//             CSSInjector.clearCSS();
	//         }
	//     };
	// }, [elements.length]);

	const renderedElements = elements.map((element) =>
		RenderHeaderElement(element, config)
	);

	return renderedElements;
};

// Complete renderer component
interface HeaderElementRendererProps {
	elements: HeaderElement[];
	selectedElementId?: string;
	onElementClick?: (element: HeaderElement) => void;
	onElementRemove?: (elementId: string) => void;
	eventHandlers?: EventHandlerRegistry;
	containerProps?: React.HTMLAttributes<HTMLDivElement>;
}

export const HeaderElementRenderer: React.FC<HeaderElementRendererProps> = ({
	elements,
	selectedElementId,
	onElementClick,
	onElementRemove,
	eventHandlers = {},
	containerProps = {},
}) => {
	const config: RendererConfig = {
		eventHandlers,
		selectedElementId,
		onElementClick,
		onElementRemove,
	};

	const renderedElements = useHeaderElementRenderer(elements, config);

	return (
		<div {...containerProps}>
			{renderedElements}
			<style>
				{elements.map((element) => element.css || "").join("\n")}
				{ACTION_BUTTONS_CSS}
			</style>
		</div>
	);
};

// Utility function to create event handler registry
export const createEventHandlerRegistry = (
	handlers: Record<string, Function>
): EventHandlerRegistry => {
	const registry: EventHandlerRegistry = {};

	Object.entries(handlers).forEach(([name, handler]) => {
		registry[name] = (...args: any[]) => handler(...args);
	});

	return registry;
};

// Now the main HeaderBuilderRenderer component
interface HeaderBuilderRendererProps {
	state: HeaderState; // Replace with your HeaderState type
	viewportSize: ViewportSize; // Replace with your ViewportSize type
	selectedComponentId: string | null;
	onComponentClick: (id: string) => void;
	onComponentRemove: (id: string) => void;
}

// Converter function from HeaderComponent to HeaderElement
/**
 * Converts a header component configuration object into a HeaderElement
 *
 * @param component - The header component configuration object containing:
 *   - type: Component type ('logo'|'searchBar'|'navItems'|'wishlist'|'cart'|'user'|'menu')
 *   - id: Unique identifier for the component
 *   - settings: Component-specific settings like image, size, placeholder etc.
 *   - styles: Custom styles for the component
 *
 * @param viewportSize - The current viewport size (desktop, tablet, mobile)
 * @param publishMode - Whether the header is being rendered in publish mode. Defaults to false
 *
 * @returns A HeaderElement object configured based on the component type, or null if type is not recognized
 *
 * Supported component types and their specific settings:
 * - logo: image, size, alt
 * - searchBar: size, design, placeholder, icon, width
 * - navItems: items array, fontWeight, textDecoration, fontStyle, gap
 * - wishlist/cart/user/menu: icon, size, shape, background, hoverColor, hoverAnimation, borderRadius
 *
 * @example
 * ```ts
 * const logoComponent = {
 *   type: 'logo',
 *   id: 'logo-1',
 *   settings: {
 *     image: '/logo.png',
 *     size: '120px',
 *     alt: 'Company Logo'
 *   },
 *   styles: {}
 * };
 * const element = convertHeaderComponentToElement(logoComponent, 'desktop', false);
 * ```
 */
export const convertHeaderComponentToElement = (
	component: HeaderComponent, // Replace with your HeaderComponent type
	viewportSize: string,
	publishMode = false
): HeaderElement | null => {
	// Convert based on component type
	switch (component.type) {
		case "logo":
			return createLogoElement(
				{
					id: component.id,
					image: component.settings?.image || "/default-logo.png",
					size: component.settings?.size || "50px",
					alt: component.settings?.alt || "Logo",
					hasHoverActions: true,
					// customStyles: component.styles
				},
				publishMode
			);

		case "searchBar":
			return createSearchBarElement(
				{
					id: component.id,
					size: component.settings?.size || "full",
					design: component.settings?.design || "default",
					placeholder: component.settings?.placeholder || "Search...",
					icon: component.settings?.icon || "search",
					width: component.styles?.width || "250px",
					customStyles: component.styles,
				},
				publishMode
			);

		case "navItems":
			return createNavItemsElement(
				{
					id: component.id,
					items: component.settings?.items || [
						{
							id: "nav-1",
							text: "Home",
							url: "/",
							isActive: false,
						},
						{
							id: "nav-2",
							text: "About",
							url: "/about",
							isActive: false,
						},
					],
					fontWeight: component.settings?.fontWeight || "normal",
					textDecoration:
						component.settings?.textDecoration || "none",
					fontStyle: component.settings?.fontStyle || "normal",
					gap: component.styles?.gap || "16px",
					customStyles: component.styles,
					menuSlug: component.settings?.menuSlug || "",
				},
				publishMode
			);

		case "wishlist":
		case "cart":
		case "user":
		case "menu":
			return createIconButtonElement(
				{
					id: component.id,
					icon: component.settings?.icon || component.type,
					size: component.settings?.size || "md",
					shape: component.settings?.shape || "rounded",
					background:
						component.settings?.background ||
						component.styles?.background ||
						"transparent",
					hoverColor: component.settings?.hoverColor || "#f3f4f6",
					hoverAnimation: component.settings?.hoverAnimation || "pop",
					borderRadius: component.settings?.borderRadius || 4,
					customStyles: component.styles,
				},
				publishMode
			);

		default:
			return null;
	}
};

// Main renderer component for HeaderDesignBuilder
const HeaderBuilderRenderer: React.FC<HeaderBuilderRendererProps> = ({
	state,
	viewportSize,
	selectedComponentId,
	onComponentClick,
	onComponentRemove,
}) => {
	// Convert HeaderComponents to HeaderElements
	const headerElements = useMemo(() => {
		return state.components
			.map((component: any) =>
				convertHeaderComponentToElement(component, viewportSize)
			)
			.filter(Boolean) as HeaderElement[];
	}, [state.components, viewportSize]);

	// Create the main header layout element
	const headerLayoutElement = useMemo(() => {
		return createHeaderLayoutElement({
			id: "main-header-layout",
			layout: state.layout,
			containerType:
				state.containerType === "container"
					? "container"
					: "full-width",
			isSticky: state.isSticky,
			headerStyles: state.styles,
			components: headerElements,
			viewportSize: viewportSize,
		});
	}, [state, headerElements, viewportSize]);

	// Create event handler registry
	const eventHandlers = useMemo(() => {
		return createEventHandlerRegistry({
			// Component interaction handlers
			handleComponentClick: (element: HeaderElement) => {
				onComponentClick(element.id);
			},
			handleComponentRemove: (elementId: string) => {
				onComponentRemove(elementId);
			},

			// Logo handlers
			handleLogoClick: (element: HeaderElement) => {
				onComponentClick(element.id);
			},
			handleLogoRemove: (elementId: string) => {
				onComponentRemove(elementId);
			},
			handleLogoEdit: (element: HeaderElement) => {
				onComponentClick(element.id);
			},

			// SearchBar handlers
			handleSearchBarClick: (element: HeaderElement) => {
				onComponentClick(element.id);
			},
			handleSearchBarRemove: (elementId: string) => {
				onComponentRemove(elementId);
			},
			handleSearchBarEdit: (element: HeaderElement) => {
				onComponentClick(element.id);
			},
			handleSearchInputClick: (e: React.MouseEvent) => {
				e.stopPropagation();
			},

			// NavItems handlers
			handleNavItemsClick: (element: HeaderElement) => {
				onComponentClick(element.id);
			},
			handleNavItemsRemove: (elementId: string) => {
				onComponentRemove(elementId);
			},
			handleNavItemsEdit: (element: HeaderElement) => {
				onComponentClick(element.id);
			},
			handleNavItemClick: (e: React.MouseEvent, item: any) => {
				e.stopPropagation();
				console.log("Nav item clicked:", item);
			},

			// IconButton handlers
			handleIconButtonClick: (element: HeaderElement) => {
				onComponentClick(element.id);
			},
			handleIconButtonRemove: (elementId: string) => {
				onComponentRemove(elementId);
			},
			handleIconButtonEdit: (element: HeaderElement) => {
				onComponentClick(element.id);
			},
			handleButtonClick: (e: React.MouseEvent) => {
				e.stopPropagation();
			},

			// Generic handlers
			handleClick: (e: React.MouseEvent) => {
				e.stopPropagation();
			},
			handleEditClick: (e: React.MouseEvent) => {
				e.stopPropagation();
			},
			handleRemoveClick: (e: React.MouseEvent) => {
				e.stopPropagation();
			},
		});
	}, [onComponentClick, onComponentRemove]);

	return (
		<HeaderElementRenderer
			elements={[headerLayoutElement]}
			selectedElementId={selectedComponentId || undefined}
			onElementClick={(element) => onComponentClick(element.id)}
			onElementRemove={onComponentRemove}
			eventHandlers={eventHandlers}
			containerProps={{
				style: {
					width: "100%",
					height: "auto",
				},
			}}
		/>
	);
};

export default HeaderBuilderRenderer;
